import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { fetchPoliticians, fetchParties } from "@/lib/api";
import Seo from "@/components/Seo";
import { Search, TrendingDown, TrendingUp, Users } from "lucide-react";
import { downloadCsv, today as todayStr } from "@/lib/csv";
import { Download } from "lucide-react";

const SORTS = [
    { value: "truth", label: "Dårligst sandhed først" },
    { value: "truth_desc", label: "Bedst sandhed først" },
    { value: "mentions", label: "Flest omtaler først" },
    { value: "name", label: "Alfabetisk" },
];

export default function Politicians() {
    const [politicians, setPoliticians] = useState([]);
    const [parties, setParties] = useState([]);
    const [sort, setSort] = useState("mentions");
    const [partyFilter, setPartyFilter] = useState("");
    const [query, setQuery] = useState("");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all([
            fetchPoliticians({ sort, ...(partyFilter ? { party_id: partyFilter } : {}) }),
            fetchParties(),
        ]).then(([pols, parts]) => {
            setPoliticians(pols);
            setParties(parts);
            setLoading(false);
        });
    }, [sort, partyFilter]);

    const filtered = useMemo(() => {
        if (!query.trim()) return politicians;
        const q = query.toLowerCase();
        return politicians.filter(
            (p) =>
                p.name.toLowerCase().includes(q) ||
                p.party_name.toLowerCase().includes(q),
        );
    }, [politicians, query]);

    return (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="politicians-page">
            <Seo
                title="Folketingsmedlemmer under lup"
                description="228+ danske folketingsmedlemmer med individuel sandhedsscore baseret på dokumenterede løgne, valgflæsk og artikelomtaler. Opdateres dagligt fra ft.dk."
                keywords="Folketingsmedlemmer, danske politikere, sandhedsscore, Mette Frederiksen, Troels Lund Poulsen, ft.dk, Folketinget"
                path="/politikere"
            />
            <header className="mb-8">
                <div className="flex items-center gap-2 mb-2">
                    <Users className="w-5 h-5 text-royal" />
                    <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                        Folketingsmedlemmer
                    </span>
                </div>
                <h1 className="font-serif-dm text-4xl sm:text-5xl text-royal mb-3">
                    Politikere under lup
                </h1>
                <p className="text-[#4F5A7A] max-w-3xl leading-relaxed">
                    Individuel sandheds-score pr. politiker. Scoren beregnes ud fra antal gange
                    politikeren er nævnt i vores artikeldatabase kombineret med hvor ofte det
                    handler om løgne eller valgflæsk. Opdateres automatisk hver dag kl. 12:10.
                </p>
            </header>

            {/* Filters */}
            <div className="flex flex-wrap gap-3 items-center mb-8 bg-white border border-[#E5E8F0] rounded-2xl p-4">
                <div className="relative flex-1 min-w-[220px]">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8A94AE]" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Søg efter politiker eller parti..."
                        data-testid="politician-search"
                        className="w-full pl-9 pr-3 py-2 text-sm border border-[#E5E8F0] rounded-full bg-[#F7F8FC] focus:outline-none focus:border-royal"
                    />
                </div>
                <select
                    value={partyFilter}
                    onChange={(e) => setPartyFilter(e.target.value)}
                    data-testid="politician-party-filter"
                    className="px-3 py-2 text-sm border border-[#E5E8F0] rounded-full bg-white text-[#0B1A3A]"
                >
                    <option value="">Alle partier</option>
                    {parties.map((p) => (
                        <option key={p.id} value={p.id}>
                            {p.name}
                        </option>
                    ))}
                </select>
                <select
                    value={sort}
                    onChange={(e) => setSort(e.target.value)}
                    data-testid="politician-sort"
                    className="px-3 py-2 text-sm border border-[#E5E8F0] rounded-full bg-white text-[#0B1A3A]"
                >
                    {SORTS.map((s) => (
                        <option key={s.value} value={s.value}>
                            {s.label}
                        </option>
                    ))}
                </select>
                <button
                    onClick={() => {
                        const rows = [["Politiker", "Parti", "Rolle", "Sandhedsscore", "Omtaler", "Løgne", "Valgflæsk"]];
                        filtered.forEach((p) => {
                            rows.push([p.name, p.party_name, p.role, p.truth_score, p.mentions_count, p.lies_count, p.valgflaesk_count]);
                        });
                        downloadCsv(rows, `politikere-${todayStr()}.csv`);
                    }}
                    data-testid="politicians-csv-download"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-royal hover:bg-royal-deep text-white rounded-full text-sm font-semibold transition-colors"
                >
                    <Download className="w-4 h-4" /> CSV
                </button>
            </div>

            {/* Grid */}
            {loading ? (
                <p className="text-[#4F5A7A]">Indlæser politikere...</p>
            ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {filtered.map((p) => (
                        <PoliticianCard key={p.id} politician={p} />
                    ))}
                    {filtered.length === 0 && (
                        <p className="text-[#4F5A7A] col-span-full">Ingen politikere matcher søgningen.</p>
                    )}
                </div>
            )}
        </div>
    );
}

function PoliticianCard({ politician }) {
    const { name, party_short_name, party_color, party_name, role, truth_score, mentions_count, lies_count, valgflaesk_count } = politician;
    const initials = name
        .split(" ")
        .map((n) => n[0])
        .slice(0, 2)
        .join("");
    const isCritical = mentions_count > 0 && truth_score < 50;
    const isGood = mentions_count === 0 || truth_score >= 80;

    return (
        <Link
            to={`/politikere/${politician.id}`}
            className="bg-white border border-[#E5E8F0] rounded-2xl p-5 hover-lift block"
            data-testid={`politician-card-${politician.id}`}
        >
            <div className="flex items-start gap-3 mb-4">
                <div
                    className="w-12 h-12 rounded-full flex items-center justify-center font-serif-dm text-lg font-bold text-white flex-shrink-0"
                    style={{ backgroundColor: party_color }}
                >
                    {initials}
                </div>
                <div className="min-w-0">
                    <div className="font-semibold text-royal leading-tight truncate">{name}</div>
                    <div className="text-xs text-[#4F5A7A] mt-0.5 flex items-center gap-1.5">
                        <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: party_color }}
                        />
                        {party_name} ({party_short_name})
                    </div>
                    <div className="text-[11px] text-[#8A94AE] mt-0.5 uppercase tracking-wider">{role}</div>
                </div>
            </div>

            <div className="flex items-center justify-between pt-3 border-t border-[#EEF0F7]">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-[#8A94AE]">Sandhedsscore</div>
                    <div className="flex items-baseline gap-1.5">
                        <span
                            className="font-serif-dm text-2xl"
                            style={{ color: isCritical ? "#BB1B2D" : isGood ? "#186A3B" : "#C78300" }}
                        >
                            {truth_score}%
                        </span>
                        {isCritical && <TrendingDown className="w-4 h-4 text-[#BB1B2D]" />}
                        {isGood && mentions_count > 0 && <TrendingUp className="w-4 h-4 text-[#186A3B]" />}
                    </div>
                </div>
                <div className="text-right text-[11px] text-[#4F5A7A] leading-snug">
                    <div>
                        <strong className="text-royal">{mentions_count}</strong> omtaler
                    </div>
                    <div className="text-[#BB1B2D]">
                        {lies_count} løgne · {valgflaesk_count} valgflæsk
                    </div>
                </div>
            </div>
        </Link>
    );
}
